// client/src/pages/ArticleForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/ArticleForm.css';

const ArticleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(isEditMode);
  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    resume: '',
    categorie: 'actualite',
    auteur: '',
    dateLecture: 3,
    featured: false,
    publie: false,
    datePublication: new Date().toISOString().split('T')[0]
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Catégories disponibles  
  const categories = [
	  { id: 'actualite', label: 'Actualité' },
	  { id: 'analyse', label: 'Analyse' },
	  { id: 'interview', label: 'Interview' },
	  { id: 'evenement', label: 'Événement' },
	  { id: 'infographies', label: 'Infographies' },
	  { id: 'leon14', label: 'Leon XIV' }
	];
  
  // Image de fallback pour les articles sans image
  const defaultImage = '/images/article-default.png';
  
  // Charger les données de l'article en mode édition
  useEffect(() => {
    const fetchArticle = async () => {
      if (!isEditMode) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`/api/articles/id/${id}`);
        const article = response.data;
        
        setFormData({
          titre: article.titre || '',
          contenu: article.contenu || '',
          resume: article.resume || '',
          categorie: article.categorie || 'actualite',
          auteur: article.auteur || '',
          dateLecture: article.dateLecture || 3,
          featured: article.featured || false,
          publie: article.publie || false,
          datePublication: article.datePublication ? new Date(article.datePublication).toISOString().split('T')[0] : ''
        });
        
        if (article.image) {
          setCurrentImage(article.image);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement de l\'article:', err);
        setError('Erreur lors du chargement de l\'article');
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id, isEditMode]);
  
  // Gestionnaire de changement pour les champs simples
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Gestionnaire pour le chargement de l'image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Fonctions pour le formatage de texte simple
  const insertFormatting = (format) => {
    const textarea = document.getElementById('contenu');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    let formattedText = '';
    let cursorPosition = 0;

    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        cursorPosition = start + 8 + selectedText.length;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        cursorPosition = start + 4 + selectedText.length;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText}</h2>`;
        cursorPosition = start + 4 + selectedText.length;
        break;
      case 'h3':
        formattedText = `<h3>${selectedText}</h3>`;
        cursorPosition = start + 4 + selectedText.length;
        break;
      case 'ul':
        formattedText = `<ul>\n  <li>${selectedText}</li>\n</ul>`;
        cursorPosition = start + 11 + selectedText.length;
        break;
      case 'ol':
        formattedText = `<ol>\n  <li>${selectedText}</li>\n</ol>`;
        cursorPosition = start + 11 + selectedText.length;
        break;
      case 'link':
        formattedText = `<a href="#">${selectedText}</a>`;
        cursorPosition = start + 9; // Position du cursor après "href="
        break;
      case 'image':
        formattedText = `<img src="#" alt="${selectedText}" />`;
        cursorPosition = start + 10; // Position du cursor après "src="
        break;
      case 'paragraph':
        formattedText = `<p>${selectedText}</p>`;
        cursorPosition = start + 3 + selectedText.length;
        break;
      case 'linebreak':
        formattedText = `${selectedText}<br />`;
        cursorPosition = start + selectedText.length + 6;
        break;
      default:
        return;
    }

    // Insérer le texte formaté
    const newContent = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
    setFormData({
      ...formData,
      contenu: newContent
    });

    // Placer le curseur à la position appropriée (après le tag de fermeture)
    // Note: Ceci ne fonctionne que si le textarea reste focus
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    // Validation des champs requis
    if (!formData.titre || !formData.contenu || !formData.resume) {
      setError('Veuillez remplir tous les champs obligatoires');
      setSaving(false);
      return;
    }
    
    try {
      let imageUrl = currentImage;
      
      // Si une nouvelle image a été sélectionnée, l'uploader d'abord
      if (image) {
        const formData = new FormData();
        formData.append('image', image);
        
        const uploadResponse = await axios.post('/api/upload/article-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        imageUrl = uploadResponse.data.imageUrl;
      }
      
      // Préparer les données de l'article
      const articleData = {
        ...formData,
        image: imageUrl
      };
      
      let response;
      if (isEditMode) {
        // Mettre à jour l'article existant
        response = await axios.put(`/api/articles/${id}`, articleData);
      } else {
        // Créer un nouvel article
        response = await axios.post('/api/articles', articleData);
      }
      
      // Rediriger vers la page d'administration des articles
      navigate('/admin/articles');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de l\'article:', err);
      setError(`Erreur lors de l'enregistrement: ${err.response?.data?.message || err.message}`);
      setSaving(false);
    }
  };
  
  // Afficher l'aperçu du contenu HTML
  const [showPreview, setShowPreview] = useState(false);
  
  if (loading) return <div className="form-loading">Chargement de l'article...</div>;
  
  return (
    <div className="article-form-page">
      <div className="article-form-header">
        <h1>{isEditMode ? 'Modifier l\'article' : 'Créer un nouvel article'}</h1>
        <Link to="/admin/articles" className="back-to-list">
          &laquo; Retour à la liste
        </Link>
      </div>
      
      {error && <div className="form-error">{error}</div>}
      
      <form onSubmit={handleSubmit} className="article-form">
        <div className="form-columns">
          <div className="form-main-column">
            <div className="form-group">
              <label htmlFor="titre">Titre *</label>
              <input
                type="text"
                id="titre"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="resume">Résumé *</label>
              <textarea
                id="resume"
                name="resume"
                value={formData.resume}
                onChange={handleChange}
                required
                rows="3"
                className="form-control"
              ></textarea>
              <div className="form-help">Ce résumé sera utilisé dans les listes d'articles et les méta-descriptions</div>
            </div>
            
            <div className="form-group">
              <label htmlFor="contenu">Contenu *</label>
              <div className="editor-container">
                <div className="editor-toolbar">
                  <button type="button" onClick={() => insertFormatting('bold')} title="Gras">B</button>
                  <button type="button" onClick={() => insertFormatting('italic')} title="Italique">I</button>
                  <button type="button" onClick={() => insertFormatting('h2')} title="Titre niveau 2">H2</button>
                  <button type="button" onClick={() => insertFormatting('h3')} title="Titre niveau 3">H3</button>
                  <button type="button" onClick={() => insertFormatting('paragraph')} title="Paragraphe">P</button>
                  <button type="button" onClick={() => insertFormatting('linebreak')} title="Saut de ligne">BR</button>
                  <button type="button" onClick={() => insertFormatting('ul')} title="Liste à puces">• Liste</button>
                  <button type="button" onClick={() => insertFormatting('ol')} title="Liste numérotée">1. Liste</button>
                  <button type="button" onClick={() => insertFormatting('link')} title="Lien">Lien</button>
                  <button type="button" onClick={() => insertFormatting('image')} title="Image">Image</button>
                  <button 
                    type="button" 
                    onClick={() => setShowPreview(!showPreview)} 
                    className={showPreview ? 'active' : ''}
                    title="Aperçu"
                  >
                    {showPreview ? 'Éditer' : 'Aperçu'}
                  </button>
                </div>
                
                {showPreview ? (
                  <div 
                    className="content-preview"
                    dangerouslySetInnerHTML={{ __html: formData.contenu }}
                  ></div>
                ) : (
                  <textarea
                    id="contenu"
                    name="contenu"
                    value={formData.contenu}
                    onChange={handleChange}
                    required
                    rows="15"
                    className="form-control editor-textarea"
                    placeholder="Saisissez votre contenu HTML ici..."
                  ></textarea>
                )}
                
                <div className="editor-help">
                  <p>Vous pouvez utiliser les balises HTML pour formater votre contenu ou utiliser les boutons ci-dessus.</p>
                  <p>Exemples: &lt;p&gt;Paragraphe&lt;/p&gt;, &lt;strong&gt;Gras&lt;/strong&gt;, &lt;em&gt;Italique&lt;/em&gt;, &lt;br /&gt; pour un saut de ligne, etc.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-sidebar">
            <div className="form-card">
              <h3>Publication</h3>
              
              <div className="form-group">
                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="publie"
                    name="publie"
                    checked={formData.publie}
                    onChange={handleChange}
                  />
                  <label htmlFor="publie">Publier l'article</label>
                </div>
                <div className="form-help">L'article sera visible sur le site si cette option est activée</div>
              </div>
              
              <div className="form-group">
                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                  />
                  <label htmlFor="featured">Mettre en avant sur la page d'accueil</label>
                </div>
                <div className="form-help">L'article apparaîtra dans la section "À la une"</div>
              </div>
              
              <div className="form-group">
                <label htmlFor="datePublication">Date de publication</label>
                <input
                  type="date"
                  id="datePublication"
                  name="datePublication"
                  value={formData.datePublication}
                  onChange={handleChange}
                  className="form-control"
                />
                <div className="form-help">Cette date sera utilisée pour trier les articles (défaut: date actuelle)</div>
              </div>
            </div>
            
            <div className="form-card">
              <h3>Informations</h3>
              
              <div className="form-group">
                <label htmlFor="categorie">Catégorie</label>
                <select
                  id="categorie"
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  className="form-control"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="auteur">Auteur</label>
                <input
                  type="text"
                  id="auteur"
                  name="auteur"
                  value={formData.auteur}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="La rédaction"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="dateLecture">Temps de lecture (minutes)</label>
                <input
                  type="number"
                  id="dateLecture"
                  name="dateLecture"
                  value={formData.dateLecture}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  className="form-control"
                />
              </div>
            </div>
            
            <div className="form-card">
              <h3>Image principale</h3>
              
              <div className="form-group">
                {(currentImage || imagePreview) && (
                  <div className="image-preview-container">
                    <img 
                      src={imagePreview || currentImage || defaultImage} 
                      alt="Aperçu de l'image" 
                      className="image-preview"
                    />
                  </div>
                )}
                
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="form-control file-input"
                />
                <div className="form-help">Format recommandé: 1200 x 630 pixels, JPG ou PNG</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-submit"
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : isEditMode ? 'Mettre à jour l\'article' : 'Publier l\'article'}
          </button>
          
          <Link to="/admin/articles" className="btn-cancel">
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ArticleForm;